import { type PlanType } from '@/types';
import { type QuizType } from '@/types/Quiz';

export type PlanFeatureType =
  | 'PRIVATE_QUIZ'
  | 'CONTINUE_NEXT_QUIZSET'
  | 'LMS_PARTIAL'
  | 'WORKSHEET'
  | 'AI_FROM_FILE'
  | 'AI_FROM_QUERY'
  | 'AI_FROM_YOUTUBE'
  | 'AI_FROM_WEBSITE'
  | 'AI_CHOICE_GENERATION'
  | 'AI_ANSWER_GENERATION'
  | 'AI_SIMILAR_QUIZ_GENERATION'
  | 'AI_QUIZSET_TRANSLATION'
  | 'AI_TUTOR'
  | 'AI_ACHIEVEMENT_STANDARD'
  | 'AI_GENERATION_THOROUGH'
  | 'REMOVE_ADS'
  | 'MATCH'
  | 'ORDER'
  | 'CLASSROOM_POINT';

export const QUIZ_TYPE_PLAN_FEATURE = {
  match: 'MATCH',
  order: 'ORDER',
} as const satisfies Partial<Record<QuizType, PlanFeatureType>>;

export type PlanGatedQuizType = keyof typeof QUIZ_TYPE_PLAN_FEATURE;

export const isPlanGatedQuizType = (
  type: QuizType,
): type is PlanGatedQuizType => type in QUIZ_TYPE_PLAN_FEATURE;

const KR_FREE_FEATURES = new Set<PlanFeatureType>([]);

const KR_BASIC_FEATURES = new Set<PlanFeatureType>([
  ...KR_FREE_FEATURES,
  'PRIVATE_QUIZ',
  'AI_FROM_FILE',
  'AI_FROM_QUERY',
  'AI_FROM_YOUTUBE',
  'AI_GENERATION_THOROUGH',
  // 서버 PointService.grant 는 hasPlan(= plan != FREE) 만 확인한다. BASIC 부터 열어 서버와 맞춘다.
  'CLASSROOM_POINT',
  // 문항 카드 AI 액션(PRD 2026-09-07 §4): 이번 단계는 BASIC 부터 연다.
  'AI_CHOICE_GENERATION',
  'AI_ANSWER_GENERATION',
  'AI_SIMILAR_QUIZ_GENERATION',
  'MATCH',
  'ORDER',
]);

const KR_PRO_FEATURES = new Set<PlanFeatureType>([
  ...KR_BASIC_FEATURES,
  'CONTINUE_NEXT_QUIZSET',
  'LMS_PARTIAL',
  'WORKSHEET',
  'AI_QUIZSET_TRANSLATION',
  'AI_TUTOR',
  'AI_ACHIEVEMENT_STANDARD',
  'AI_FROM_WEBSITE',
]);

const GLOBAL_FREE_FEATURES = new Set<PlanFeatureType>([]);

const GLOBAL_BASIC_FEATURES = new Set<PlanFeatureType>([
  ...GLOBAL_FREE_FEATURES,
  'PRIVATE_QUIZ',
  'AI_FROM_FILE',
  'AI_FROM_QUERY',
  'AI_FROM_YOUTUBE',
  'AI_GENERATION_THOROUGH',
  'REMOVE_ADS',
  'CLASSROOM_POINT',
  // 문항 카드 AI 액션(PRD 2026-09-07 §4): 이번 단계는 BASIC 부터 연다.
  'AI_CHOICE_GENERATION',
  'AI_ANSWER_GENERATION',
  'AI_SIMILAR_QUIZ_GENERATION',
  'MATCH',
  'ORDER',
]);

const GLOBAL_PRO_FEATURES = new Set<PlanFeatureType>([
  ...GLOBAL_BASIC_FEATURES,
  'CONTINUE_NEXT_QUIZSET',
  'LMS_PARTIAL',
  'WORKSHEET',
  'AI_QUIZSET_TRANSLATION',
  'AI_FROM_WEBSITE',
  // 'AI_TUTOR', 현재는 한국만 지원
]);

export const PLAN_FEATURES: Record<
  'KR' | 'GLOBAL',
  Record<PlanType, Set<PlanFeatureType>>
> = {
  KR: {
    FREE: KR_FREE_FEATURES,
    BASIC: KR_BASIC_FEATURES,
    PRO: KR_PRO_FEATURES,
  },
  GLOBAL: {
    FREE: GLOBAL_FREE_FEATURES,
    BASIC: GLOBAL_BASIC_FEATURES,
    PRO: GLOBAL_PRO_FEATURES,
  },
};
